//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import { Box, Typography } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import BuildIcon from '@mui/icons-material/Build';
import PsychologyIcon from '@mui/icons-material/Psychology';
import VisibilityIcon from '@mui/icons-material/Visibility';

import { type TraceEntry } from '@/agent/contract';

interface TraceListProps {
  entries: TraceEntry[];
}

const KIND_LABEL: Record<TraceEntry['kind'], string> = {
  thought: 'Thought',
  tool: 'Tool call',
  result: 'Result'
};

const KindIcon = ({ kind }: { kind: TraceEntry['kind'] }) => {
  const props = { fontSize: 'inherit', 'aria-hidden': true } as const;
  switch (kind) {
    case 'thought': return <PsychologyIcon {...props} />;
    case 'tool': return <BuildIcon {...props} />;
    case 'result': return <VisibilityIcon {...props} />;
  }
};

// The agent's progress under a pending reply, or the steps a stopped turn
// completed under its stop entry.  A list, so a screen reader hears how many
// steps there are; the kind is said in text as well as drawn.
const TraceList = ({ entries }: TraceListProps) => {
  if (entries.length === 0) return null;
  return (
    <Box component="ul" sx={{ listStyle: 'none', m: 0, pl: '17px',
                              color: 'text.secondary', fontSize: 13 }}>
      {entries.map((entry) => (
        <Box component="li" key={entry.step}
             sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <KindIcon kind={entry.kind} />
          <Typography component="span" sx={visuallyHidden}>
            {KIND_LABEL[entry.kind]}:
          </Typography>
          <Typography component="span" variant="body2">
            {entry.summary}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

export default TraceList;
